import React, { useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

function FAQItem({ q, a, index }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });

  return (
    <motion.div
      ref={ref}
      className={`faq-item ${open ? 'faq-item-open' : ''}`}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.06 }}
      onClick={() => setOpen(!open)}
    >
      <div className="faq-q">
        <span className="faq-num">0{index + 1}</span>
        <h3>{q}</h3>
        <motion.div animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.3 }}>
          <ChevronDown size={20} />
        </motion.div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            className="faq-a"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            <p>{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function FAQ() {
  const faqs = [
    { q: 'How do I submit a demo to CloudfiMusic?', a: 'Visit our Demo Submission page and fill out the form with your artist name, links to your music, and a brief bio. We review all submissions within 14 business days. Please ensure your music fits within the lofi, ambient, or chill genres.' },
    { q: 'What genres does CloudfiMusic accept?', a: 'We specialize in lofi, ambient, chillhop, downtempo, and related subgenres. If your music evokes calm, focus, or reflection, it may be a good fit. We listen to every submission with an open mind.' },
    { q: 'What is the royalty split?', a: 'Our standard split is 70/30 (label/artist) for label-funded releases and 50/50 for artist-funded productions. Specific terms are discussed individually with each artist before signing.' },
    { q: 'How long is the exclusivity period?', a: 'All releases are subject to a 12-month exclusivity period from the release date, with automatic renewal unless either party provides 60 days written notice of non-renewal.' },
    { q: 'Can I release music on other platforms while signed?', a: 'During the exclusivity period, tracks released through CloudfiMusic cannot be distributed through other labels or distributors. You retain the right to release new, unreleased music independently.' },
    { q: 'How are royalties paid?', a: 'Royalties are calculated and paid quarterly (every 3 months), processed within 45 days of the quarter ending. The minimum payout threshold is $50 USD.' },
    { q: 'Can I license CloudfiMusic tracks for my project?', a: 'Yes! Visit our Licensing page for information on sync licensing for films, ads, games, YouTube content, and more. Contact info@cloudfimusic.com for custom licensing requests.' },
    { q: 'How do I contact CloudfiMusic?', a: 'You can reach us through the Contact page on our website, or email us directly at info@cloudfimusic.com. We typically respond within 2-3 business days.' },
    { q: 'Is CloudfiMusic hiring?', a: 'Check our Careers page for current openings. We are always interested in passionate people who share our love for lofi and ambient music.' },
  ];

  return (
    <div className="faq-page">
      <div className="faq-hero">
        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >Frequently Asked<br /><span>Questions</span></motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >Everything you need to know about CloudfiMusic</motion.p>
      </div>

      <div className="faq-list">
        {faqs.map((f, i) => (
          <FAQItem key={i} q={f.q} a={f.a} index={i} />
        ))}
      </div>
    </div>
  );
}
